import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import QRCode from 'qrcode';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { Modal } from './ui/Modal';
import {
  IntercomConfig,
  getIntercomConfigs,
  createIntercomConfig,
  updateIntercomConfig,
  deleteIntercomConfig,
  regenerateKioskCode,
} from '../api/intercomConfig';
import { apiList } from '../api/apiClient';
import { apiOrigin, visitorOrigin } from '../utils/surface';
import { deviceAccessId, eligibleUnlockDevices } from '../utils/deviceCapabilities';

interface Props {
  propertyId: number | string;
  devices: any[];
  telnyxNumbers?: string[];
  /** Property's units — used by the per-intercom resident-hide picker. */
  units?: Array<{ id: string | number; unit_number: string; floor?: string | null }>;
  /** Property-level fallbacks for fields that overlay on intercom_config.
      When the intercom row has no value, the voice lambda falls back to
      these — the modals show them as the effective current value. */
  propertyDefaults?: Record<string, any>;
}

// Generates a 600px QR PNG **locally** via the `qrcode` library — no network,
// no cross-origin image fetch, no CSP/connect-src dependency. Creates a
// throwaway canvas on click, asks qrcode to draw onto it, then toBlob() →
// object URL → synthetic anchor click. Works under any CSP that allows the
// page itself to render.
const QRDownloadButton: React.FC<{ value: string; name: string }> = ({ value, name }) => {
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          const canvas = document.createElement('canvas');
          await QRCode.toCanvas(canvas, value, {
            width: 600,
            margin: 2,
            errorCorrectionLevel: 'H',
            color: { dark: '#000000', light: '#ffffff' },
          });
          const trigger = (href: string, revoke?: () => void) => {
            const a = document.createElement('a');
            a.href = href;
            a.download = name;
            a.rel = 'noopener';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            if (revoke) setTimeout(revoke, 1000);
          };
          await new Promise<void>((resolve) => {
            canvas.toBlob((blob) => {
              if (blob && blob.size > 0) {
                const url = URL.createObjectURL(blob);
                trigger(url, () => URL.revokeObjectURL(url));
              } else {
                // Fallback for older browsers — dataURL is always non-empty.
                trigger(canvas.toDataURL('image/png'));
              }
              resolve();
            }, 'image/png');
          });
        } catch (err) {
          console.error('QR download failed:', err);
          alert('Failed to generate QR. Check console for details.');
        } finally {
          setBusy(false);
        }
      }}
      className="text-brand-600 hover:text-brand-700 disabled:opacity-50"
      title="Download QR (600px PNG)"
    >{busy ? 'Saving…' : 'Download PNG'}</button>
  );
};

const deviceKey = (d: any) => String(d?.uuid || d?.id || '');
const deviceLabel = (d: any) => {
  if (!d) return '';
  const name = d.name || `Device ${d.id}`;
  const type = d.type || d.device_type;
  const tag = d.acs_device_id ? `${(d.acs_provider || 'PDK').toUpperCase()} ${d.acs_device_id}` : type;
  return tag ? `${name} (${tag})` : name;
};

// Settings that the visitor directory consumes must survive the public API,
// not just the authenticated write endpoint. A 200 from the editor is not
// enough to tell a PM that the intercom actually changed for visitors.
const PUBLIC_VISITOR_SETTINGS = new Set([
  'enabled', 'intercom_enabled', 'phone_number', 'hidden_unit_ids',
  'pin_access_enabled', 'guest_pin_access_enabled', 'face_access_enabled',
  'allow_phone_call', 'allow_video_call', 'greeting_message', 'directory_display',
  'private_directory', 'display_style', 'accent_color', 'background_color',
  'logo_url', 'logo_size_px', 'show_powered_by', 'custom_footer_text',
  'auto_lock_enabled', 'auto_lock_start', 'auto_lock_end',
  'require_visitor_name', 'require_visitor_phone', 'hide_vacant_units',
  'show_floor_grouping', 'show_phone_on_directory', 'show_tenant_count',
  'custom_no_answer_text', 'unit_sort_order', 'directory_name_format',
  'visitor_code_response',
]);

export const IntercomDoorConfig: React.FC<Props> = ({ propertyId, devices, telnyxNumbers = [], units = [], propertyDefaults = {} }) => {
  const [configs, setConfigs] = useState<IntercomConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | number | 'new' | null>(null);
  const [deletingId, setDeletingId] = useState<string | number | null>(null);

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getIntercomConfigs(propertyId);
      setConfigs(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Failed to load intercom configs:', e);
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => { fetchConfigs(); }, [fetchConfigs]);

  // Build a quick lookup so we can show device names in chips.
  const devicesById = useMemo(() => {
    const m = new Map<string, any>();
    for (const d of devices) m.set(deviceKey(d), d);
    return m;
  }, [devices]);

  const handleAddIntercom = async () => {
    setSavingId('new');
    try {
      await createIntercomConfig({
        property_id: propertyId,
        phone_number: telnyxNumbers[0] || '',
        door_device_ids: [],
        label: configs.length === 0 ? 'Main Intercom' : `Intercom #${configs.length + 1}`,
        enabled: true,
      });
      await fetchConfigs();
    } catch (e: any) {
      console.error(e);
      alert(`Failed to create intercom: ${e?.message || 'unknown error'}`);
    } finally {
      setSavingId(null);
    }
  };

  const updateField = async (config: IntercomConfig, patch: Partial<IntercomConfig>) => {
    // Optimistic UI so toggles + dropdowns feel instant.
    setConfigs((prev) => prev.map((c) => (c.id === config.id ? { ...c, ...patch } : c)));
    setSavingId(config.id);
    try {
      const saved = await updateIntercomConfig(config.id, patch);
      // The API used to silently discard columns absent from PG while still
      // returning 200. Never report a setting as saved unless it reads back.
      for (const [key, value] of Object.entries(patch)) {
        if (JSON.stringify((saved as any)[key] ?? null) !== JSON.stringify(value ?? null)) {
          throw new Error(`${key} was not persisted by the intercom API`);
        }
      }
      const publicKeys = Object.keys(patch).filter((key) => PUBLIC_VISITOR_SETTINGS.has(key));
      if (publicKeys.length > 0) {
        const response = await fetch(
          `${apiOrigin()}/public/intercom-configs?property_id=${encodeURIComponent(String(propertyId))}`,
          { cache: 'no-store' },
        );
        if (!response.ok) throw new Error('Visitor directory settings could not be verified');
        const rows = await response.json();
        const publicRow = (Array.isArray(rows) ? rows : []).find((row: any) => String(row.id) === String(config.id));
        if (!publicRow) throw new Error('Intercom is missing from the visitor directory');
        for (const key of publicKeys) {
          if (JSON.stringify(publicRow[key] ?? null) !== JSON.stringify((patch as any)[key] ?? null)) {
            throw new Error(`${key} was saved but is not visible to the visitor directory`);
          }
        }
      }
      setConfigs((prev) => prev.map((c) => (c.id === config.id ? saved : c)));
    } catch (e: any) {
      console.error('Failed to update intercom:', e);
      // Roll back on failure.
      setConfigs((prev) => prev.map((c) => (c.id === config.id ? config : c)));
      alert(`Failed to update: ${e?.message || 'unknown error'}`);
      throw e;
    } finally {
      setSavingId(null);
    }
  };

  const handleRegenerateKiosk = async (config: IntercomConfig) => {
    if (!window.confirm('Generate a new kiosk activation code? Any panel currently paired with this intercom will be unpaired.')) return;
    try {
      const resp = await regenerateKioskCode(config.id);
      setConfigs((prev) => prev.map((c) => (c.id === config.id ? {
        ...c,
        kiosk_code: resp.kiosk_code,
        kiosk_code_expires_at: resp.kiosk_code_expires_at ?? null,
      } : c)));
    } catch (e: any) {
      alert(`Failed to regenerate code: ${e?.message || 'unknown error'}`);
    }
  };

  const handleDelete = async (id: number | string) => {
    try {
      await deleteIntercomConfig(id as number);
      await fetchConfigs();
      setDeletingId(null);
    } catch (e: any) {
      alert(`Failed to remove: ${e?.message || 'unknown error'}`);
    }
  };

  const toggleDoor = async (config: IntercomConfig, devKey: string) => {
    const cur = (config.door_device_ids || []).map(String);
    const next = cur.includes(devKey) ? cur.filter((x) => x !== devKey) : [...cur, devKey];
    await updateField(config, { door_device_ids: next });
  };

  // Render — guarantee the user always sees at least one default intercom card.
  const list: (IntercomConfig | null)[] = configs.length > 0 ? configs : [null];

  return (
    <div className="space-y-3">
      {/* Section header sits above the cards (no outer Card wrapping everything). */}
      <div className="flex items-center justify-between px-1">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Intercoms</h3>
          <p className="text-[11px] text-gray-500 mt-0.5">
            One card per intercom — each gets its own phone, kiosk code, devices, and visitor URL.
          </p>
        </div>
        <Button size="sm" onClick={handleAddIntercom} disabled={savingId === 'new'} className="bg-accent-500 hover:bg-accent-600">
          <span className="inline-flex items-center gap-1">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>
            {savingId === 'new' ? 'Adding…' : 'Add Intercom'}
          </span>
        </Button>
      </div>

      {loading ? (
        <Card>
          <div className="text-center text-gray-400 text-sm py-4">Loading…</div>
        </Card>
      ) : (
        // Single-column full-width cards — easier to scan and gives each card
        // room for the per-category action row below the header.
        <div className="space-y-3">
          {list.map((config, idx) => {
            // Build a map of "phones taken by some OTHER intercom" → its label,
            // so each row's dropdown can disable / annotate already-used numbers
            // and enforce the one-phone-per-intercom rule.
            const phonesUsedElsewhere: Record<string, string> = {};
            for (const c of configs) {
              if (!c?.phone_number) continue;
              if (config && c.id === config.id) continue;
              phonesUsedElsewhere[String(c.phone_number)] = c.label || `Intercom ${c.id}`;
            }
            return (
              <IntercomRow
                key={config?.id ?? `placeholder-${idx}`}
                index={idx}
                config={config}
                devices={devices}
                devicesById={devicesById}
                telnyxNumbers={telnyxNumbers}
                phonesUsedElsewhere={phonesUsedElsewhere}
                totalIntercoms={configs.length}
                units={units}
                saving={savingId === (config?.id ?? '__none__')}
                onUpdate={(patch) => config ? updateField(config, patch) : Promise.resolve()}
                onRegenerateKiosk={() => config && handleRegenerateKiosk(config)}
                onToggleDoor={(devKey) => config && toggleDoor(config, devKey)}
                onRequestDelete={() => config && setDeletingId(config.id)}
                onCreateDefault={handleAddIntercom}
                propertyDefaults={propertyDefaults}
              />
            );
          })}
        </div>
      )}

      <Modal open={deletingId !== null} onClose={() => setDeletingId(null)} title="Remove Intercom">
        <p className="text-sm text-gray-700 mb-1">Remove this intercom configuration?</p>
        <p className="text-xs text-gray-500 mb-4">Visitor calls routed through it will stop working until another intercom is set up.</p>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setDeletingId(null)}>Cancel</Button>
          <Button className="!bg-red-600 hover:!bg-red-700" onClick={() => deletingId !== null && handleDelete(deletingId)}>Remove</Button>
        </div>
      </Modal>
    </div>
  );
};

// ── Label editor with local state + commit-on-blur/Enter ──────────────────
// Saving on every keystroke spams the API and lets a stale refresh stomp
// the user's input mid-type. Buffer locally and only persist when focus
// leaves the field (or the user presses Enter).
const LabelInput: React.FC<{ value: string; placeholder?: string; onCommit: (v: string) => void }> = ({ value, placeholder, onCommit }) => {
  const [local, setLocal] = useState(value);
  const [focused, setFocused] = useState(false);
  const lastCommitted = React.useRef(value);
  useEffect(() => {
    if (value !== lastCommitted.current) {
      setLocal(value);
      lastCommitted.current = value;
    }
  }, [value]);
  const commit = () => {
    setFocused(false);
    if (local !== lastCommitted.current) {
      lastCommitted.current = local;
      onCommit(local);
    }
  };
  return (
    <div className="flex-1 min-w-0">
      <label className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-0.5">
        Intercom name
      </label>
      <div className="flex items-center gap-1.5 group">
        <input
          value={local}
          placeholder={placeholder}
          onChange={(e) => setLocal(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
          }}
          className={`flex-1 min-w-0 text-base font-semibold text-gray-900 px-2 py-1 rounded-md border transition-colors ${
            focused
              ? 'border-accent-500 bg-white ring-2 ring-accent-100'
              : 'border-gray-300 bg-white hover:border-accent-400'
          }`}
        />
        <svg
          className="w-4 h-4 text-gray-400 group-hover:text-accent-500 transition-colors flex-shrink-0"
          fill="none" viewBox="0 0 24 24" stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
        </svg>
      </div>
    </div>
  );
};

// ── Single intercom row (its own card-like block) ───────────────────────────
interface RowProps {
  index: number;
  config: IntercomConfig | null;
  devices: any[];
  devicesById: Map<string, any>;
  telnyxNumbers: string[];
  /** Phones already claimed by other intercoms on this property → owning label. */
  phonesUsedElsewhere: Record<string, string>;
  /** Total intercoms on this property — drives whether the visitor URL needs
   *  the per-intercom segment. With one intercom, the bare property URL is fine
   *  (and the kiosk app routes the unlock to that single intercom anyway). */
  totalIntercoms: number;
  units: Array<{ id: string | number; unit_number: string; floor?: string | null }>;
  saving: boolean;
  onUpdate: (patch: Partial<IntercomConfig>) => Promise<void>;
  onRegenerateKiosk: () => void;
  onToggleDoor: (devKey: string) => void;
  onRequestDelete: () => void;
  onCreateDefault: () => void;
  propertyDefaults?: Record<string, any>;
}

const IntercomRow: React.FC<RowProps> = ({
  index, config, devices, devicesById, telnyxNumbers, phonesUsedElsewhere, totalIntercoms, units,
  saving, onUpdate, onRegenerateKiosk, onToggleDoor, onRequestDelete, onCreateDefault,
  propertyDefaults = {},
}) => {
  const [doorPickerOpen, setDoorPickerOpen] = useState(false);
  const [unitsPickerOpen, setUnitsPickerOpen] = useState(false);
  // Phase 1 of the per-intercom modal redesign: clicking a category opens
  // a modal scaffold. Phase 2 will fill each modal with the matching
  // settings form (currently still rendered on the Settings tab).
  type ModalKey =
    | null
    | 'voice'
    | 'video'
    | 'branding'
    | 'devices'
    | 'access'
    | 'mobile'
    | 'deliveries'
    | 'residents'
    | 'delete';
  const [openModal, setOpenModal] = useState<ModalKey>(null);
  const hiddenUnitIds = useMemo(
    () => new Set((config?.hidden_unit_ids ?? []).map(String)),
    [config?.hidden_unit_ids]
  );
  const visibleUnitsCount = units.length - hiddenUnitIds.size;
  const toggleUnitHidden = (unitId: string) => {
    const next = new Set(hiddenUnitIds);
    if (next.has(unitId)) next.delete(unitId); else next.add(unitId);
    void onUpdate({ hidden_unit_ids: Array.from(next) }).catch(() => {});
  };

  // Placeholder card for when the property has no intercom yet.
  if (!config) {
    return (
      <Card>
        <div className="text-center py-2">
          <p className="text-sm font-medium text-gray-700">No intercom configured yet.</p>
          <p className="text-xs text-gray-500 mt-1">Create one to start routing visitor calls and pair the kiosk app.</p>
          <Button size="sm" onClick={onCreateDefault} className="mt-3 bg-accent-500 hover:bg-accent-600">Create First Intercom</Button>
        </div>
      </Card>
    );
  }

  const assignedKeys = (config.door_device_ids || []).map(String);
  const enabled = config.enabled !== false;
  // Every persisted intercom uses the explicit scoped visitor route. This
  // prevents a property-level redirect from selecting a different intercom
  // after another configuration is added or reordered.
  const intercomKey = (config as any).uuid || (config as any).intercom_config_uuid || config.id;
  const visitorUrl = `${visitorOrigin()}/${config.property_id}/i/${intercomKey}`;
  // ecc=H = 30% error correction → safe to overlay a logo (≤20% of QR area).
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=120x120&margin=2&ecl=H&data=${encodeURIComponent(visitorUrl)}`;

  return (
    <Card padding={false} className="overflow-hidden !border-gray-300">
      {/* ── Header strip: editable name + phone number (stacked) ──────────── */}
      <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-b from-gray-50 to-white space-y-3">
        <LabelInput
          value={config.label || ''}
          placeholder={`Intercom #${index + 1}`}
          onCommit={(next) => { void onUpdate({ label: next }).catch(() => {}); }}
        />
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-0.5">Phone</p>
          {config.phone_number ? (
            <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-white border border-gray-200">
              <svg className="w-3.5 h-3.5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
              </svg>
              <span className="font-mono text-sm font-semibold text-gray-900">{config.phone_number}</span>
            </div>
          ) : (
            <span className="text-xs text-gray-400 italic">Video only</span>
          )}
        </div>
      </div>

      {/* ── QR + visitor URL (above settings) ─────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 p-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <a href={qrSrc} target="_blank" rel="noreferrer" className="block bg-white border border-gray-200 rounded p-1" title="Open large QR">
            <img src={qrSrc} alt="QR" width={96} height={96} className="block" />
          </a>
          <div className="space-y-1">
            <a
              href={visitorUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold bg-accent-500 hover:bg-accent-600 text-white whitespace-nowrap"
              title={`Open ${visitorUrl}`}
            >
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 3h7m0 0v7m0-7L10 14M5 5h4a2 2 0 012 2v4M5 5v14a2 2 0 002 2h12a2 2 0 002-2v-7"/></svg>
              View Directory
            </a>
            <div className="flex items-center gap-2 text-[10px] font-medium">
              <button
                onClick={() => navigator.clipboard?.writeText(visitorUrl)}
                className="text-brand-600 hover:text-brand-700"
                title="Copy visitor URL"
              >Copy URL</button>
              <span className="text-gray-300">·</span>
              <QRDownloadButton value={visitorUrl} name={`${(config.label || 'intercom').toLowerCase().replace(/\s+/g, '-')}-qr.png`} />
            </div>
            <p className="text-[10px] text-gray-400 font-mono break-all max-w-xs">{visitorUrl}</p>
          </div>
        </div>
        <div className="text-[11px] text-gray-500 flex items-center gap-2 self-center">
          {saving && <span className="italic">Saving…</span>}
        </div>
      </div>

      {/* ── Settings panel ────────────────────────────────────────────────── */}
      <div className="px-4 pt-3 pb-3 border-b border-gray-100 bg-white">
        <div className="flex items-center justify-between gap-2 mb-2">
          <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Settings</p>
          <button
            onClick={() => { void onUpdate({ enabled: !enabled }).catch(() => {}); }}
            className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full transition-colors ${enabled ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
            title="Toggle whether this intercom is active"
          >
            <span className={`w-1.5 h-1.5 rounded-full ${enabled ? 'bg-green-500' : 'bg-gray-400'}`} />
            {enabled ? 'Enabled' : 'Disabled'}
          </button>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {([
            { key: 'voice',      label: 'Voice Settings',     desc: 'Phone greetings, no-answer messages, ring duration, call timeout' },
            { key: 'video',      label: 'Video Settings',     desc: 'Allow video calls, ring strategy, in-call unlock, call duration limits' },
            { key: 'branding',   label: 'Display & Branding', desc: 'Visitor screen theme, accent colors, logo, welcome message' },
            { key: 'devices',    label: 'Intercom Devices',   desc: 'Label, phone number (DID), kiosk code, assigned doors' },
            { key: 'access',     label: 'Access & Security',  desc: 'PIN unlock, face recognition, auto-lock, geofence rules' },
            { key: 'mobile',     label: 'Mobile App Settings', desc: 'Tenant guest limits and app activation behavior' },
            { key: 'deliveries', label: 'Delivery Settings',  desc: 'Allow delivery PINs, delivery doors, driver message' },
            { key: 'residents',  label: 'Resident Visibility', desc: 'Hide/show units in this intercom\'s directory' },
          ] as Array<{ key: Exclude<ModalKey, null>; label: string; desc: string }>).map((b) => (
            <div key={b.key} className="flex flex-col gap-1">
              <button
                type="button"
                onClick={() => setOpenModal(b.key)}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-[12px] font-semibold text-white bg-accent-500 hover:bg-accent-600 transition-colors shadow-sm w-full"
              >
                <svg className="w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                </svg>
                <span className="truncate">{b.label}</span>
              </button>
              <p className="text-[11px] leading-snug text-gray-700 px-1">{b.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Danger zone (bottom of card) ──────────────────────────────────── */}
      <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 flex justify-end">
        <button
          type="button"
          onClick={() => setOpenModal('delete' as any)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-semibold text-red-600 bg-white border border-red-200 hover:bg-red-50 transition-colors shadow-sm"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3"/>
          </svg>
          Delete Intercom
        </button>
      </div>

      {/* ── Category modal — phase 2: per-category body, real save ──────────
          Voice is the first category to migrate. The rest stay as scaffolds
          until they're populated. All save through the existing onUpdate(patch)
          path, which writes via PUT /intercom-configs/<id> (legacy-id-resolved
          + phantom-hardened). */}
      <Modal
        open={openModal !== null}
        onClose={() => setOpenModal(null)}
        size="lg"
        title={(() => {
          const t: Record<Exclude<ModalKey, null>, string> = {
            voice:     'Voice Settings',
            video:     'Video Settings',
            branding:  'Display & Branding',
            devices:   'Intercom Devices',
            access:    'Access & Security',
            mobile:    'Mobile App',
            deliveries: 'Delivery Settings',
            residents: 'Residents',
            delete:    'Delete Intercom',
          };
          return openModal ? `${t[openModal]} — ${config.label || `Intercom #${index + 1}`}` : '';
        })()}
      >
        {openModal === 'voice' ? (
          <VoiceModalBody
            config={config}
            propertyDefaults={propertyDefaults}
            saving={saving}
            onSave={async (patch) => { await onUpdate(patch); }}
            onClose={() => setOpenModal(null)}
          />
        ) : openModal === 'video' ? (
          <VideoModalBody
            config={config}
            saving={saving}
            onSave={async (patch) => { await onUpdate(patch); }}
            onClose={() => setOpenModal(null)}
          />
        ) : openModal === 'branding' ? (
          <BrandingModalBody
            config={config}
            saving={saving}
            onSave={async (patch) => { await onUpdate(patch); }}
            onClose={() => setOpenModal(null)}
          />
        ) : openModal === 'devices' ? (
          <DevicesModalBody
            config={config}
            devices={devices}
            telnyxNumbers={telnyxNumbers}
            phonesUsedElsewhere={phonesUsedElsewhere}
            saving={saving}
            onSave={async (patch) => { await onUpdate(patch); }}
            onRegenerateKiosk={onRegenerateKiosk}
            onClose={() => setOpenModal(null)}
          />
        ) : openModal === 'access' ? (
          <AccessModalBody
            config={config}
            propertyDefaults={propertyDefaults}
            saving={saving}
            onSave={async (patch) => { await onUpdate(patch); }}
            onClose={() => setOpenModal(null)}
          />
        ) : openModal === 'mobile' ? (
          <MobileModalBody
            config={config}
            devices={devices}
            saving={saving}
            onSave={async (patch) => { await onUpdate(patch); }}
            onClose={() => setOpenModal(null)}
          />
        ) : openModal === 'deliveries' ? (
          <DeliveryModalBody
            config={config}
            devices={devices}
            saving={saving}
            onSave={async (patch) => { await onUpdate(patch); }}
            onClose={() => setOpenModal(null)}
          />
        ) : openModal === 'residents' ? (
          <ResidentsModalBody
            config={config}
            units={units}
            saving={saving}
            onSave={async (patch) => { await onUpdate(patch); }}
            onClose={() => setOpenModal(null)}
          />
        ) : openModal === 'delete' ? (
          <DeleteIntercomModalBody
            intercomName={config.label || `Intercom #${index + 1}`}
            onConfirm={() => { setOpenModal(null); onRequestDelete(); }}
            onClose={() => setOpenModal(null)}
          />
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              Settings for this category will move here in the next pass. For now
              it still lives on the property's <strong>Settings</strong> tab, scoped
              to the intercom you select there.
            </p>
            <p className="text-xs text-gray-500">
              Intercom: <span className="font-mono">{config.label || `#${index + 1}`}</span>
              {' · '}
              <span className="font-mono">
                {((config as any).uuid || (config as any).intercom_config_uuid || config.id || '').toString().slice(0, 8)}
              </span>
            </p>
            <div className="flex justify-end pt-1">
              <Button variant="secondary" onClick={() => setOpenModal(null)}>Close</Button>
            </div>
          </div>
        )}
      </Modal>
    </Card>
  );
};

// ── Voice modal body ──────────────────────────────────────────────────────
// Phase-2 form for the Voice category. Local state initialised from the
// intercom_config row; explicit Save button writes a patch back via onSave,
// which is wired to the parent's optimistic updateField → PUT
// /intercom-configs/<id>. Empty values are sent as null so the backend
// falls back to property-level defaults (matches the per-intercom overlay
// in telnyx-texml-webhook us-east-1).
const VoiceModalBody: React.FC<{
  config: IntercomConfig;
  propertyDefaults?: Record<string, any>;
  saving: boolean;
  onSave: (patch: Partial<IntercomConfig>) => Promise<void>;
  onClose: () => void;
}> = ({ config, propertyDefaults = {}, saving, onSave, onClose }) => {
  const c = config as any;
  // Effective resolver — intercom override → property fallback → hardcoded
  // default. The voice lambda follows the same precedence (`pick()` helper
  // in /tmp/twh-test.mjs), so the modal mirrors what's actually heard.
  const eff = <T,>(key: string, hardcoded: T): T =>
    (c[key] != null && c[key] !== '' ? c[key] : (propertyDefaults[key] != null && propertyDefaults[key] !== '' ? propertyDefaults[key] : hardcoded));
  const overriddenByIntercom = (key: string) => c[key] != null && c[key] !== '';

  const [voiceGreeting,     setVoiceGreeting]     = useState<string>(c.voice_greeting_message ?? '');
  const [visitorConnecting, setVisitorConnecting] = useState<string>(c.visitor_connecting_message ?? '');
  const [tenantAnnounce,    setTenantAnnounce]    = useState<string>(c.tenant_call_announcement ?? '');
  // Auto-migrate any legacy Azure voice name to the matching Polly neural
  // voice so the dropdown shows the actual voice playing at the door.
  const AZURE_TO_POLLY_UI: Record<string, string> = {
    'Azure.en-US-JennyNeural':      'Polly.Joanna',
    'Azure.en-US-SaraNeural':       'Polly.Salli',
    'Azure.en-US-AriaNeural':       'Polly.Kendra',
    'Azure.en-US-GuyNeural':        'Polly.Gregory',
    'Azure.en-US-DavisNeural':      'Polly.Stephen',
    'Azure.en-GB-SoniaNeural':      'Polly.Amy',
    'Azure.en-GB-RyanNeural':       'Polly.Brian',
    'Azure.es-US-PalomaNeural':     'Polly.Lupe',
    'Azure.es-US-AlonsoNeural':     'Polly.Pedro',
    'Azure.es-MX-DaliaNeural':      'Polly.Lupe',
    'Azure.fr-FR-DeniseNeural':     'Polly.Lea',
    'Azure.fr-FR-HenriNeural':      'Polly.Mathieu',
    'Azure.pt-BR-FranciscaNeural':  'Polly.Camila',
    'Azure.zh-CN-XiaoxiaoNeural':   'Polly.Zhiyu',
    'Azure.zh-CN-YunxiNeural':      'Polly.Zhiyu',
  };
  const initialVoice = eff<string>('voice_name', 'Polly.Joanna');
  const [voiceName,         setVoiceName]         = useState<string>(AZURE_TO_POLLY_UI[initialVoice] || initialVoice);
  const [voiceSpeed,        setVoiceSpeed]        = useState<string>(eff<string>('voice_speed', '-10%'));
  const [voicePitch,        setVoicePitch]        = useState<string>(eff<string>('voice_pitch', 'medium'));
  const [voiceVolume,       setVoiceVolume]       = useState<string>(eff<string>('voice_volume', 'medium'));
  const [voiceLanguage,     setVoiceLanguage]     = useState<string>(eff<string>('voice_language', 'en-US'));
  const [ringTone,          setRingTone]          = useState<string>(eff<string>('ring_tone', 'us'));
  const [dtmfDigit,         setDtmfDigit]         = useState<string>(c.dtmf_unlock_digit ?? c.dtmf_action_digit ?? eff<string>('dtmf_unlock_digit', '9'));
  const [dwellTime,         setDwellTime]         = useState<string>(String(eff<number | string>('dwell_time', 5)));
  // Simring (simultaneous ring) settings — used when a unit has multiple
  // tenants. Defaults match today's prod behavior so leaving them alone
  // changes nothing.
  const [simRing,           setSimRing]           = useState<boolean>(c.simultaneous_ring != null ? c.simultaneous_ring !== false : (propertyDefaults.simultaneous_ring !== false));
  const [maxRings,          setMaxRings]          = useState<string>(String(eff<number | string>('max_rings', 10)));
  const [unavailableMessage, setUnavailableMessage] = useState<string>(c.unavailable_message ?? '');
  const [previewLoading,    setPreviewLoading]    = useState<string | null>(null); // tracks which one is playing
  const [previewError,      setPreviewError]      = useState<string>('');

  const handleSave = async () => {
    await onSave({
      voice_greeting_message:     voiceGreeting     || null,
      visitor_connecting_message: visitorConnecting || null,
      tenant_call_announcement:   tenantAnnounce    || null,
      voice_name:     voiceName || null,
      voice_speed:    voiceSpeed || null,
      voice_pitch:    voicePitch || null,
      voice_volume:   voiceVolume || null,
      voice_language: voiceLanguage || null,
      ring_tone:      ringTone || null,
      dtmf_unlock_digit: dtmfDigit || null,
      dwell_time:     dwellTime ? Number(dwellTime) : null,
      simultaneous_ring:   simRing,
      max_rings:           maxRings ? Number(maxRings) : null,
      unavailable_message: unavailableMessage || null,
    } as any);
    onClose();
  };

  // Preview any of the three messages with the same TTS settings so PMs
  // can hear exactly what each surface will speak.
  const previewText = async (key: 'greeting' | 'visitor' | 'tenant', text: string, fallback: string) => {
    setPreviewError('');
    setPreviewLoading(key);
    try {
      const res = await fetch(`${apiOrigin()}/voice/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: text || fallback,
          voice_name: voiceName,
          voice_speed: voiceSpeed,
          voice_volume: voiceVolume,
        }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      const blob = new Blob([Uint8Array.from(atob(data.audio), (x: string) => x.charCodeAt(0))], { type: 'audio/mpeg' });
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.onended = () => URL.revokeObjectURL(url);
      await audio.play();
    } catch (e: any) {
      setPreviewError(e?.message || String(e));
    } finally {
      setPreviewLoading(null);
    }
  };

  const labelCls  = 'block text-xs font-medium text-gray-600 mb-1';
  const inputCls  = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-accent-500 focus:border-accent-500';

  // Tag rendered beside a label that calls out where the effective value
  // is coming from. "Intercom override" = stored on this intercom_config
  // row. "Property default" = inherited from the property; saving this
  // modal writes a per-intercom override.
  const sourceTag = (key: string) => {
    const intercomHasIt = overriddenByIntercom(key);
    const propertyHasIt = propertyDefaults[key] != null && propertyDefaults[key] !== '';
    if (intercomHasIt) {
      return <span className="ml-2 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-accent-100 text-accent-700">Intercom override</span>;
    }
    if (propertyHasIt) {
      return <span className="ml-2 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">Property default</span>;
    }
    return <span className="ml-2 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">Built-in default</span>;
  };

  const previewBtn = (key: 'greeting' | 'visitor' | 'tenant', text: string, fallback: string) => (
    <button
      type="button"
      disabled={previewLoading === key}
      onClick={() => previewText(key, text, fallback)}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md text-white bg-accent-500 hover:bg-accent-600 disabled:opacity-50"
    >
      {previewLoading === key ? 'Playing…' : 'Preview'}
    </button>
  );

  return (
    <div className="space-y-4 max-h-[65vh] overflow-y-auto">
      {/* ── Messages ─────────────────────────────────────────────────── */}
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2">Messages</p>
        <p className="text-xs text-gray-500 -mt-1 mb-2">
          The voice settings below apply to every message on this intercom so all spoken prompts sound consistent.
        </p>
      </div>

      <div>
        <label className={labelCls}>Phone Greeting (caller hears when calling the intercom phone number){sourceTag('voice_greeting_message')}</label>
        <textarea
          value={voiceGreeting}
          onChange={(e) => setVoiceGreeting(e.target.value)}
          placeholder="Hello, welcome to [Property Name]. Please enter the apartment number you are visiting, followed by the pound key."
          rows={3}
          className={inputCls}
        />
        <p className="text-[11px] text-gray-400 mt-1">
          Played by the phone IVR when a caller dials this intercom. Falls back to the property default when left blank.
        </p>
        <div className="mt-2 flex items-center gap-2">
          {previewBtn('greeting', voiceGreeting, 'Hello, welcome. Please enter the apartment number you are visiting, followed by the pound key.')}
        </div>
      </div>

      <div>
        <label className={labelCls}>Visitor Connecting Message (visitor hears while we ring the tenant){sourceTag('visitor_connecting_message')}</label>
        <textarea
          value={visitorConnecting}
          onChange={(e) => setVisitorConnecting(e.target.value)}
          placeholder="One moment please, connecting you now."
          rows={2}
          className={inputCls}
        />
        <p className="text-[11px] text-gray-400 mt-1">
          Played on the visitor side (web kiosk or phone IVR) after they pick a unit and the tenant is being dialed. Leave blank to use the default: "One moment please, connecting you now."
        </p>
        <div className="mt-2 flex items-center gap-2">
          {previewBtn('visitor', visitorConnecting, 'One moment please, connecting you now.')}
        </div>
      </div>

      <div>
        <label className={labelCls}>Tenant Call Announcement (tenant hears when answering){sourceTag('tenant_call_announcement')}</label>
        <textarea
          value={tenantAnnounce}
          onChange={(e) => setTenantAnnounce(e.target.value)}
          placeholder="Connecting you now."
          rows={2}
          className={inputCls}
        />
        <p className="text-[11px] text-gray-400 mt-1">
          Whisper played to the tenant on answer. Leave blank to use the default: "Connecting you now."
        </p>
        <div className="mt-2 flex items-center gap-2">
          {previewBtn('tenant', tenantAnnounce, 'Connecting you now.')}
          {previewError && <span className="text-[11px] text-red-600">{previewError}</span>}
        </div>
      </div>

      {/* ── Voice (applies to all messages) ──────────────────────────── */}
      <div className="pt-3 border-t border-gray-100">
        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">Voice (applies to all messages)</p>
        <p className="text-[11px] text-gray-500">
          Every prompt — phone greeting, visitor connecting message, and tenant call announcement — is rendered with Amazon Polly Neural TTS. Preview audio is byte-identical to what the door speaks. Speed and volume apply live; Polly Neural does not support pitch.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Voice{sourceTag('voice_name')}</label>
          <select value={voiceName} onChange={(e) => setVoiceName(e.target.value)} className={inputCls}>
            <optgroup label="English (US)">
              <option value="Polly.Joanna">Joanna (Female)</option>
              <option value="Polly.Salli">Salli (Female)</option>
              <option value="Polly.Kendra">Kendra (Female)</option>
              <option value="Polly.Ruth">Ruth (Female)</option>
              <option value="Polly.Gregory">Gregory (Male)</option>
              <option value="Polly.Stephen">Stephen (Male)</option>
              <option value="Polly.Matthew">Matthew (Male)</option>
            </optgroup>
            <optgroup label="English (UK)">
              <option value="Polly.Amy">Amy (Female)</option>
              <option value="Polly.Brian">Brian (Male)</option>
              <option value="Polly.Arthur">Arthur (Male)</option>
            </optgroup>
            <optgroup label="Spanish">
              <option value="Polly.Lupe">Lupe (Female, US)</option>
              <option value="Polly.Pedro">Pedro (Male, US)</option>
              <option value="Polly.Mia">Mia (Female, MX)</option>
            </optgroup>
            <optgroup label="French">
              <option value="Polly.Lea">Léa (Female, FR)</option>
              <option value="Polly.Mathieu">Mathieu (Male, FR)</option>
            </optgroup>
            <optgroup label="Portuguese">
              <option value="Polly.Camila">Camila (Female, BR)</option>
              <option value="Polly.Vitoria">Vitória (Female, BR)</option>
            </optgroup>
            <optgroup label="Chinese">
              <option value="Polly.Zhiyu">Zhiyu (Female, CN)</option>
            </optgroup>
          </select>
        </div>
        <div>
          <label className={labelCls}>Language{sourceTag('voice_language')}</label>
          <select value={voiceLanguage} onChange={(e) => setVoiceLanguage(e.target.value)} className={inputCls}>
            <option value="en-US">English (US)</option>
            <option value="en-GB">English (UK)</option>
            <option value="es-US">Spanish (US)</option>
            <option value="es-MX">Spanish (Mexico)</option>
            <option value="fr-FR">French</option>
            <option value="pt-BR">Portuguese (Brazil)</option>
            <option value="zh-CN">Chinese (Mandarin)</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className={labelCls}>Speed{sourceTag('voice_speed')}</label>
          <select value={voiceSpeed} onChange={(e) => setVoiceSpeed(e.target.value)} className={inputCls}>
            <option value="-25%">Slow (-25%)</option>
            <option value="-10%">Default (-10%)</option>
            <option value="0%">Normal</option>
            <option value="+10%">Fast (+10%)</option>
            <option value="+25%">Faster (+25%)</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>Pitch</label>
          <select disabled value={voicePitch} onChange={(e) => setVoicePitch(e.target.value)} className={`${inputCls} bg-gray-50 text-gray-400 cursor-not-allowed`}>
            <option value="x-low">Very Low</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="x-high">Very High</option>
          </select>
          <p className="text-[10px] text-gray-400 mt-1">Not supported by Polly Neural voices.</p>
        </div>
        <div>
          <label className={labelCls}>Volume{sourceTag('voice_volume')}</label>
          <select value={voiceVolume} onChange={(e) => setVoiceVolume(e.target.value)} className={inputCls}>
            <option value="silent">Silent</option>
            <option value="x-soft">Very Soft</option>
            <option value="soft">Soft</option>
            <option value="medium">Medium</option>
            <option value="loud">Loud</option>
            <option value="x-loud">Very Loud</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className={labelCls}>Ring Tone{sourceTag('ring_tone')}</label>
          <select value={ringTone} onChange={(e) => setRingTone(e.target.value)} className={inputCls}>
            <option value="us">US</option>
            <option value="uk">UK</option>
            <option value="long">Long</option>
            <option value="short">Short</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>DTMF Unlock Digit{sourceTag('dtmf_unlock_digit')}</label>
          <select value={dtmfDigit} onChange={(e) => setDtmfDigit(e.target.value)} className={inputCls}>
            {['0','1','2','3','4','5','6','7','8','9','*','#'].map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
          <p className="text-[10px] text-gray-400 mt-1">Resident presses this digit during a call to unlock.</p>
        </div>
        <div>
          <label className={labelCls}>Dwell (seconds){sourceTag('dwell_time')}</label>
          <input
            type="number"
            min={1}
            max={60}
            value={dwellTime}
            onChange={(e) => setDwellTime(e.target.value)}
            className={inputCls}
          />
        </div>
      </div>

      {/* ── Simultaneous Ring ───────────────────────────────────────── */}
      <div className="pt-3 border-t border-gray-100">
        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2">Simultaneous Ring (units with multiple tenants)</p>
      </div>

      <div>
        <label className="flex items-start gap-2 cursor-pointer py-1">
          <input
            type="checkbox"
            checked={simRing}
            onChange={(e) => setSimRing(e.target.checked)}
            className="rounded border-gray-300 text-accent-600 focus:ring-accent-500 h-4 w-4 flex-shrink-0 mt-0.5"
          />
          <span>
            <span className="text-sm text-gray-700">Ring every tenant in a unit at once{sourceTag('simultaneous_ring')}</span>
            <span className="block text-[11px] text-gray-400">
              When off, only the first tenant on the unit is rung. When on, all tenants ring in parallel and the first to answer wins. The tenant announcement (above) is whispered to the winner before the visitor is bridged in.
            </span>
          </span>
        </label>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className={labelCls}>Max rings before unavailable{sourceTag('max_rings')}</label>
          <input
            type="number"
            min={1}
            max={30}
            value={maxRings}
            onChange={(e) => setMaxRings(e.target.value)}
            className={inputCls}
          />
          <p className="text-[10px] text-gray-400 mt-1">~6 seconds per ring. Default 10.</p>
        </div>
        <div className="col-span-2">
          <label className={labelCls}>Unavailable message{sourceTag('unavailable_message')}</label>
          <input
            type="text"
            value={unavailableMessage}
            onChange={(e) => setUnavailableMessage(e.target.value)}
            placeholder="I'm sorry, the tenant is currently unavailable and did not answer."
            className={inputCls}
          />
          <p className="text-[10px] text-gray-400 mt-1">Played to the caller after the max rings expires. Leave blank to use the default.</p>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} disabled={saving} className="bg-accent-500 hover:bg-accent-600">
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
};

// ── Video modal body ──────────────────────────────────────────────────────
// Per-intercom video-call routing knobs. Backend columns are SINGULAR
// (`allow_video_call`, `allow_phone_call`) — match exactly. The plural
// legacy keys were never written server-side so we only read singular.
const VideoModalBody: React.FC<{
  config: IntercomConfig;
  saving: boolean;
  onSave: (patch: Partial<IntercomConfig>) => Promise<void>;
  onClose: () => void;
}> = ({ config, saving, onSave, onClose }) => {
  const c = config as any;
  const [allowVideo,        setAllowVideo]        = useState<boolean>(c.allow_video_call !== false);
  const [simultaneousRing,  setSimultaneousRing]  = useState<boolean>(c.simultaneous_ring !== false);
  const [allowUnlockInCall, setAllowUnlockInCall] = useState<boolean>(c.allow_door_unlock_in_call !== false);
  const [callTimeout,       setCallTimeout]       = useState<string>(String(c.call_timeout_seconds ?? c.call_timeout ?? '45'));
  const [ringDuration,      setRingDuration]      = useState<string>(String(c.ring_duration_seconds ?? c.ring_duration ?? '30'));
  const [noAnswerMsg,       setNoAnswerMsg]       = useState<string>(c.no_answer_message ?? '');
  const [callBlockedMsg,    setCallBlockedMsg]    = useState<string>(c.call_blocked_message ?? '');

  const handleSave = async () => {
    await onSave({
      allow_video_call:         allowVideo,
      simultaneous_ring:        simultaneousRing,
      allow_door_unlock_in_call: allowUnlockInCall,
      call_timeout_seconds:     callTimeout ? Number(callTimeout) : null,
      ring_duration_seconds:    ringDuration ? Number(ringDuration) : null,
      no_answer_message:        noAnswerMsg || null,
      call_blocked_message:     callBlockedMsg || null,
    } as any);
    onClose();
  };

  const labelCls = 'block text-xs font-medium text-gray-600 mb-1';
  const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-accent-500 focus:border-accent-500';
  const checkRow = (checked: boolean, onChange: (v: boolean) => void, label: string, hint?: string) => (
    <label className="flex items-start gap-2 cursor-pointer py-1">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="rounded border-gray-300 text-accent-600 focus:ring-accent-500 h-4 w-4 flex-shrink-0 mt-0.5"
      />
      <span>
        <span className="text-sm text-gray-700">{label}</span>
        {hint && <span className="block text-[11px] text-gray-400">{hint}</span>}
      </span>
    </label>
  );

  return (
    <div className="space-y-4 max-h-[65vh] overflow-y-auto">
      <div className="space-y-1">
        {checkRow(allowVideo, setAllowVideo, 'Allow video calls', 'Master switch — when off, the visitor page hides the Video Call button for this intercom.')}
        {checkRow(simultaneousRing, setSimultaneousRing, 'Ring all tenants in the unit simultaneously', 'When off, the visitor hits a single tenant per ring sequence.')}
        {checkRow(allowUnlockInCall, setAllowUnlockInCall, 'Allow door unlock during a video call', 'Lets the resident tap unlock on the in-call screen.')}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Call Timeout (seconds)</label>
          <input
            type="number"
            min={5}
            max={300}
            value={callTimeout}
            onChange={(e) => setCallTimeout(e.target.value)}
            className={inputCls}
          />
          <p className="text-[11px] text-gray-400 mt-1">Total time the call waits to be answered before failing over.</p>
        </div>
        <div>
          <label className={labelCls}>Ring Duration (seconds)</label>
          <input
            type="number"
            min={5}
            max={120}
            value={ringDuration}
            onChange={(e) => setRingDuration(e.target.value)}
            className={inputCls}
          />
          <p className="text-[11px] text-gray-400 mt-1">How long each ring attempt lasts before retry.</p>
        </div>
      </div>

      <div>
        <label className={labelCls}>No-Answer Message</label>
        <input
          type="text"
          value={noAnswerMsg}
          onChange={(e) => setNoAnswerMsg(e.target.value)}
          placeholder="Sorry, the resident isn't available right now."
          className={inputCls}
        />
        <p className="text-[11px] text-gray-400 mt-1">Shown on the visitor screen when the call goes unanswered.</p>
      </div>

      <div>
        <label className={labelCls}>Call Disabled Message</label>
        <input
          type="text"
          value={callBlockedMsg}
          onChange={(e) => setCallBlockedMsg(e.target.value)}
          placeholder="Calls are currently unavailable."
          className={inputCls}
        />
        <p className="text-[11px] text-gray-400 mt-1">Shown when the visitor tries to call but video is disabled or rate-limited.</p>
      </div>

      <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} disabled={saving} className="bg-accent-500 hover:bg-accent-600">
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
};

// ── Display & Branding modal body ─────────────────────────────────────────
// Per-intercom controls for what the visitor directory page LOOKS like
// (theme + colors + logo + greeting) and what it INCLUDES (units, phones,
// floors, tenant count, sort order). All fields fall back to property
// defaults via null. Visitor directory reads these via the intercom
// overlay applied in VisitorDirectory.tsx loadData.
const BrandingModalBody: React.FC<{
  config: IntercomConfig;
  saving: boolean;
  onSave: (patch: Partial<IntercomConfig>) => Promise<void>;
  onClose: () => void;
}> = ({ config, saving, onSave, onClose }) => {
  const c = config as any;
  const [greetingMsg,        setGreetingMsg]        = useState<string>(c.greeting_message ?? c.welcome_message ?? '');
  const [directoryDisplay,   setDirectoryDisplay]   = useState<string>(c.directory_display ?? 'tenant_name');
  const [directoryNameFmt,   setDirectoryNameFmt]   = useState<string>(c.directory_name_format ?? 'First Last');
  const [unitSortOrder,      setUnitSortOrder]      = useState<string>(c.unit_sort_order ?? 'Numerical');
  const [showPhoneOnDir,     setShowPhoneOnDir]     = useState<boolean>(c.show_phone_on_directory !== false);
  const [showFloorGrouping,  setShowFloorGrouping]  = useState<boolean>(c.show_floor_grouping !== false);
  const [hideVacantUnits,    setHideVacantUnits]    = useState<boolean>(c.hide_vacant_units !== false);
  const [showTenantCount,    setShowTenantCount]    = useState<boolean>(c.show_tenant_count !== false);
  const [displayStyle,       setDisplayStyle]       = useState<string>(c.display_style ?? 'modern_dark');
  const [accentColor,        setAccentColor]        = useState<string>(c.accent_color ?? '#059669');
  const [backgroundColor,    setBackgroundColor]    = useState<string>(c.background_color ?? '#101010');
  const [logoUrl,            setLogoUrl]            = useState<string>(c.logo_url ?? '');
  const [logoSizePx,         setLogoSizePx]         = useState<string>(String(c.logo_size_px ?? '80'));
  const [customNoAnswer,     setCustomNoAnswer]     = useState<string>(c.custom_no_answer_text ?? '');
  const [customFooter,       setCustomFooter]       = useState<string>(c.custom_footer_text ?? '');

  const handleSave = async () => {
    await onSave({
      greeting_message:        greetingMsg || null,
      directory_display:       directoryDisplay || null,
      directory_name_format:   directoryNameFmt || null,
      unit_sort_order:         unitSortOrder || null,
      show_phone_on_directory: showPhoneOnDir,
      show_floor_grouping:     showFloorGrouping,
      hide_vacant_units:       hideVacantUnits,
      show_tenant_count:       showTenantCount,
      display_style:           displayStyle || null,
      accent_color:            accentColor || null,
      background_color:        backgroundColor || null,
      logo_url:                logoUrl || null,
      logo_size_px:            logoSizePx ? Number(logoSizePx) : null,
      custom_no_answer_text:   customNoAnswer || null,
      custom_footer_text:      customFooter || null,
    } as any);
    onClose();
  };

  const labelCls = 'block text-xs font-medium text-gray-600 mb-1';
  const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-accent-500 focus:border-accent-500';
  const checkRow = (checked: boolean, onChange: (v: boolean) => void, label: string) => (
    <label className="flex items-center gap-2 cursor-pointer py-1">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="rounded border-gray-300 text-accent-600 focus:ring-accent-500 h-4 w-4 flex-shrink-0"
      />
      <span className="text-sm text-gray-700">{label}</span>
    </label>
  );

  return (
    <div className="space-y-4 max-h-[65vh] overflow-y-auto">
      {/* Visitor directory greeting */}
      <div>
        <label className={labelCls}>Visitor Directory Greeting</label>
        <textarea
          value={greetingMsg}
          onChange={(e) => setGreetingMsg(e.target.value)}
          placeholder="Welcome! Please select the unit you're visiting."
          rows={2}
          className={inputCls}
        />
        <p className="text-[11px] text-gray-400 mt-1">Shown at the top of this intercom's visitor page. Separate from the phone IVR greeting (under Voice).</p>
      </div>

      {/* Directory display rules */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Directory Display Mode</label>
          <select value={directoryDisplay} onChange={(e) => setDirectoryDisplay(e.target.value)} className={inputCls}>
            <option value="tenant_name">Show Tenant Names</option>
            <option value="unit_only">Unit Number Only</option>
            <option value="display_name">Custom Display Names</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>Name Format</label>
          <select value={directoryNameFmt} onChange={(e) => setDirectoryNameFmt(e.target.value)} className={inputCls}>
            <option value="First Last">First Last</option>
            <option value="Last, First">Last, First</option>
            <option value="First Initial + Last">First Initial + Last</option>
          </select>
        </div>
      </div>

      <div>
        <label className={labelCls}>Unit Sort Order</label>
        <select value={unitSortOrder} onChange={(e) => setUnitSortOrder(e.target.value)} className={inputCls}>
          <option value="Numerical">Numerical</option>
          <option value="Alphabetical">Alphabetical</option>
          <option value="Custom">Custom</option>
        </select>
      </div>

      <div className="space-y-0.5 pt-1 border-t border-gray-100">
        {checkRow(showPhoneOnDir, setShowPhoneOnDir, 'Show phone numbers on directory')}
        {checkRow(showFloorGrouping, setShowFloorGrouping, 'Group units by floor')}
        {checkRow(hideVacantUnits, setHideVacantUnits, 'Hide vacant units')}
        {checkRow(showTenantCount, setShowTenantCount, 'Show total tenant count')}
      </div>

      {/* Theme + colors */}
      <div className="pt-3 border-t border-gray-100 space-y-3">
        <div>
          <label className={labelCls}>Display Style</label>
          <select value={displayStyle} onChange={(e) => setDisplayStyle(e.target.value)} className={inputCls}>
            <option value="modern_dark">Modern Dark</option>
            <option value="modern_light">Modern Light</option>
            <option value="classic">Classic</option>
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Accent Color</label>
            <div className="flex items-center gap-2">
              <input type="color" value={accentColor} onChange={(e) => setAccentColor(e.target.value)} className="h-9 w-14 px-1 py-1 border border-gray-300 rounded-lg cursor-pointer" />
              <input type="text" value={accentColor} onChange={(e) => setAccentColor(e.target.value)} className={`flex-1 ${inputCls}`} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Background Color</label>
            <div className="flex items-center gap-2">
              <input type="color" value={backgroundColor} onChange={(e) => setBackgroundColor(e.target.value)} className="h-9 w-14 px-1 py-1 border border-gray-300 rounded-lg cursor-pointer" />
              <input type="text" value={backgroundColor} onChange={(e) => setBackgroundColor(e.target.value)} className={`flex-1 ${inputCls}`} />
            </div>
          </div>
        </div>
      </div>

      {/* Property Logo — file upload (matches the Settings-page widget so the
          PM can pick an image, see a live preview, and tune size on a slider).
          Stored as a base64 data URL on the intercom_config row so the visitor
          SPA can render it without hitting any auth-gated endpoint. */}
      <div className="pt-3 border-t border-gray-100">
        <label className={labelCls}>Property Logo</label>
        <div className="flex items-start gap-4">
          <div className="w-20 h-20 rounded-2xl border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50 overflow-hidden flex-shrink-0">
            {logoUrl ? (
              <img src={logoUrl} alt="Property logo" className="w-full h-full object-cover" />
            ) : (
              <svg className="w-8 h-8 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <input
              id={`intercom-logo-upload-${c.id}`}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                if (file.size > 750 * 1024) {
                  alert('Logo file is too large. Please upload an image under 750KB.');
                  e.currentTarget.value = '';
                  return;
                }
                const reader = new FileReader();
                reader.onload = () => {
                  const dataUrl = String(reader.result || '');
                  setLogoUrl(dataUrl);
                };
                reader.onerror = () => alert('Failed to read the image file.');
                reader.readAsDataURL(file);
                e.currentTarget.value = '';
              }}
            />
            <div className="flex items-center gap-2">
              <label
                htmlFor={`intercom-logo-upload-${c.id}`}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer text-gray-700"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/></svg>
                {logoUrl ? 'Replace Logo' : 'Upload Logo'}
              </label>
              {logoUrl && (
                <button
                  type="button"
                  onClick={() => setLogoUrl('')}
                  className="text-xs font-medium text-red-600 hover:text-red-700"
                >Remove</button>
              )}
            </div>
            <p className="text-[11px] text-gray-500 mt-1.5">
              PNG / JPG / SVG · Max 750 KB · Replaces the building icon on this intercom's visitor page.
            </p>
          </div>
        </div>
      </div>

      {/* Logo size — range slider with live preview, matches the Settings UI. */}
      <div>
        <label className={labelCls}>
          Logo Size on Directory: <span className="font-mono">{logoSizePx}px</span>
        </label>
        <input
          type="range"
          min={40}
          max={240}
          step={4}
          value={logoSizePx}
          onChange={(e) => setLogoSizePx(e.target.value)}
          className="w-full"
        />
        <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
          <span>40px</span>
          <span>240px</span>
        </div>
        {logoUrl && (
          <div className="mt-2 flex justify-center bg-gray-50 rounded-lg p-3">
            <img
              src={logoUrl}
              alt="Logo preview"
              style={{ height: `${logoSizePx}px`, width: 'auto', maxWidth: '100%' }}
            />
          </div>
        )}
      </div>

      {/* Footer / no-answer copy */}
      <div className="pt-3 border-t border-gray-100 space-y-3">
        <div>
          <label className={labelCls}>Custom "No Answer" Text</label>
          <input
            type="text"
            value={customNoAnswer}
            onChange={(e) => setCustomNoAnswer(e.target.value)}
            placeholder="The resident is unavailable right now."
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls}>Custom Footer Text (above "Powered by Intrlock")</label>
          <input
            type="text"
            value={customFooter}
            onChange={(e) => setCustomFooter(e.target.value)}
            placeholder="Managed by ACME Properties · (555) 123-4567"
            className={inputCls}
          />
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} disabled={saving} className="bg-accent-500 hover:bg-accent-600">
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
};

// ── Intercom Devices modal body ───────────────────────────────────────────
// Identity + hardware fields for this specific intercom: label, phone DID
// (with conflict-detection against sibling intercoms), kiosk pairing code
// (regenerable in place), assigned door devices, and the enabled flag.
// Hidden_unit_ids lives in the Residents modal — it's a per-tenant control,
// not a hardware one.
const DevicesModalBody: React.FC<{
  config: IntercomConfig;
  devices: any[];
  telnyxNumbers: string[];
  phonesUsedElsewhere: Record<string, string>;
  saving: boolean;
  onSave: (patch: Partial<IntercomConfig>) => Promise<void>;
  onRegenerateKiosk: () => void;
  onClose: () => void;
}> = ({ config, devices, telnyxNumbers, phonesUsedElsewhere, saving, onSave, onRegenerateKiosk, onClose }) => {
  const c = config as any;
  const unlockDevices = eligibleUnlockDevices(devices);
  const [phoneNumber,     setPhoneNumber]     = useState<string>(c.phone_number ?? '');
  const [enabled,         setEnabled]         = useState<boolean>(c.enabled !== false);
  const [doorIds,         setDoorIds]         = useState<string[]>((c.door_device_ids ?? []).map(String));
  const conflictOwner = phoneNumber && phonesUsedElsewhere[phoneNumber];

  const toggleDoor = (k: string) => {
    setDoorIds((prev) => prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]);
  };

  const handleSave = async () => {
    if (phoneNumber && phonesUsedElsewhere[phoneNumber]) {
      alert(`That number is already assigned to "${phonesUsedElsewhere[phoneNumber]}". Free it from there first.`);
      return;
    }
    // Drop any tenant-visible-door entries pointing at doors no longer
    // assigned to this intercom — the Mobile App modal manages the rest.
    const cleanedTenantVisible: string[] | null = Array.isArray(c.tenant_visible_door_ids)
      ? (c.tenant_visible_door_ids as any[]).map(String).filter((x: string) => doorIds.includes(x))
      : (c.tenant_visible_door_ids ?? null);
    await onSave({
      phone_number: phoneNumber || null,
      enabled,
      door_device_ids: doorIds,
      tenant_visible_door_ids: cleanedTenantVisible,
    } as any);
    onClose();
  };

  const labelCls = 'block text-xs font-medium text-gray-600 mb-1';
  const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-accent-500 focus:border-accent-500';

  return (
    <div className="space-y-4 max-h-[65vh] overflow-y-auto">
      <div className="flex justify-end">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="rounded border-gray-300 text-accent-600 focus:ring-accent-500 h-4 w-4"
          />
          <span className="text-sm text-gray-700">Enabled</span>
        </label>
      </div>

      <div>
        <label className={labelCls}>Phone (DID)</label>
        {telnyxNumbers.length > 0 ? (
          <select
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            className={`${inputCls} ${conflictOwner ? 'border-red-300 bg-red-50' : ''}`}
          >
            <option value="">— Video only —</option>
            {telnyxNumbers.map((p) => {
              const ownerLabel = phonesUsedElsewhere[p];
              const isOwn = p === phoneNumber;
              const taken = !!ownerLabel && !isOwn;
              return (
                <option key={p} value={p} disabled={taken}>
                  {p}{taken ? ` — in use by ${ownerLabel}` : ''}
                </option>
              );
            })}
          </select>
        ) : (
          <input
            type="tel"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder="+15551234567"
            className={inputCls}
          />
        )}
        {conflictOwner && (
          <p className="text-[11px] text-red-600 mt-1">Conflict — also assigned to "{conflictOwner}". Each phone may only belong to one intercom.</p>
        )}
        <p className="text-[11px] text-gray-400 mt-1">Inbound phone IVR routes via this number. Leave blank for a video-only intercom.</p>
      </div>

      <div>
        <label className={labelCls}>Kiosk Pairing Code</label>
        <div className="flex items-center gap-1">
          <div className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg font-mono text-sm font-bold text-gray-900 tracking-[0.2em]">
            {c.kiosk_code || '------'}
          </div>
          <button
            type="button"
            onClick={() => c.kiosk_code && navigator.clipboard?.writeText(String(c.kiosk_code))}
            disabled={!c.kiosk_code}
            className="px-3 py-2 text-xs font-medium text-gray-600 hover:text-gray-900 disabled:opacity-30"
          >Copy</button>
          <button
            type="button"
            onClick={onRegenerateKiosk}
            className="px-3 py-2 text-xs font-medium text-gray-600 hover:text-gray-900"
          >Generate New</button>
        </div>
        <p className="text-[11px] text-gray-400 mt-1">Enter this code on the kiosk panel during install. New code unpairs any currently-paired panel.</p>
      </div>

      <div>
        <label className={labelCls}>Door Devices ({doorIds.length} selected)</label>
        {unlockDevices.length === 0 ? (
          <p className="text-[11px] text-gray-500 italic px-3 py-2 border border-gray-200 rounded-lg">No devices on this property yet.</p>
        ) : (
          <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
            {unlockDevices.map((d) => {
              const k = deviceAccessId(d);
              const checked = doorIds.includes(k);
              return (
                <label key={k} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 border-b border-gray-50 last:border-b-0">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleDoor(k)}
                    className="w-4 h-4 rounded border-gray-300 text-accent-600 focus:ring-accent-500"
                  />
                  <span className="flex-1 text-gray-700 truncate">{deviceLabel(d)}</span>
                </label>
              );
            })}
          </div>
        )}
        <p className="text-[11px] text-gray-400 mt-1">Doors this intercom is allowed to unlock during a call or via DTMF. To control which of these doors tenants see in the mobile app, open <span className="font-medium">Mobile App</span>.</p>
      </div>

      <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} disabled={saving} className="bg-accent-500 hover:bg-accent-600">
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
};

// ── Access & Security modal body ──────────────────────────────────────────
// Visitor entry methods (phone/PIN/face/guest PIN) + directory gate
// (require_directory_code) + auto-lock window + privacy/rate-limit knobs +
// geofence. Some keys (pin_access_enabled, face_access_enabled) overlap
// with the per-intercom toggles already on the card; the modal is the
// canonical edit surface going forward.
const AccessModalBody: React.FC<{
  config: IntercomConfig;
  propertyDefaults: Record<string, any>;
  saving: boolean;
  onSave: (patch: Partial<IntercomConfig>) => Promise<void>;
  onClose: () => void;
}> = ({ config, propertyDefaults, saving, onSave, onClose }) => {
  const c = config as any;
  const [allowPhone,        setAllowPhone]        = useState<boolean>(c.allow_phone_call ?? (propertyDefaults.allow_phone_call !== false));
  const [pinEnabled,        setPinEnabled]        = useState<boolean>(c.pin_access_enabled ?? (
    propertyDefaults.pin_access_enabled === true
    || propertyDefaults.guest_pin_access_enabled === true
    || propertyDefaults.delivery_access_enabled === true
  ));
  const [faceEnabled,       setFaceEnabled]       = useState<boolean>(c.face_access_enabled ?? (propertyDefaults.face_access_enabled === true));
  const [guestPinEnabled,   setGuestPinEnabled]   = useState<boolean>(c.guest_pin_access_enabled ?? (propertyDefaults.guest_pin_access_enabled === true));
  const [requireDirCode,    setRequireDirCode]    = useState<boolean>(c.require_directory_code === true);
  const [dirAccessCode,     setDirAccessCode]     = useState<string>(c.directory_access_code ?? '');
  const [visitorCodeResponse,setVisitorCodeResponse] = useState<'grant_only'|'grant_and_open_pass'>(c.visitor_code_response === 'grant_and_open_pass' ? 'grant_and_open_pass' : 'grant_only');
  const [autoLockEnabled,   setAutoLockEnabled]   = useState<boolean>(c.auto_lock_enabled === true);
  const [autoLockStart,     setAutoLockStart]     = useState<string>(c.auto_lock_start ?? '22:00');
  const [autoLockEnd,       setAutoLockEnd]       = useState<string>(c.auto_lock_end ?? '06:00');
  const [requireVisName,    setRequireVisName]    = useState<boolean>(c.require_visitor_name === true);
  const [requireVisPhone,   setRequireVisPhone]   = useState<boolean>(c.require_visitor_phone === true);
  const [privateDirectory,  setPrivateDirectory]  = useState<boolean>(c.private_directory === true);
  const [logAllAccess,      setLogAllAccess]      = useState<boolean>(c.log_all_visitor_access === true);
  const [visitorRateLimit,  setVisitorRateLimit]  = useState<string>(String(c.visitor_rate_limit ?? '5'));
  const [blockNoAnswers,    setBlockNoAnswers]    = useState<boolean>(c.block_repeated_no_answers === true);
  const [maxNoAnswers,      setMaxNoAnswers]      = useState<string>(String(c.max_no_answers_before_block ?? '3'));
  // Geofence
  const [geofenceEnabled,   setGeofenceEnabled]   = useState<boolean>(c.geofence_enabled === true);
  const [geofenceLat,       setGeofenceLat]       = useState<string>(c.geofence_lat ? String(c.geofence_lat) : '');
  const [geofenceLng,       setGeofenceLng]       = useState<string>(c.geofence_lng ? String(c.geofence_lng) : '');
  const [geofenceRadius,    setGeofenceRadius]    = useState<string>(String(c.geofence_radius_m ?? '100'));
  const initialApply = Array.isArray(c.geofence_apply_to) ? c.geofence_apply_to : ['web', 'mobile'];
  const [geofenceWeb,       setGeofenceWeb]       = useState<boolean>(initialApply.includes('web'));
  const [geofenceMobile,    setGeofenceMobile]    = useState<boolean>(initialApply.includes('mobile'));
  // PIN-entry photo capture
  const [pinPhotoEnabled,   setPinPhotoEnabled]   = useState<boolean>(c.pin_entry_photo_enabled === true);
  const [pinPhotoRequired,  setPinPhotoRequired]  = useState<boolean>(c.pin_entry_photo_required === true);
  const initialKinds: string[] = Array.isArray(c.pin_entry_photo_kinds) && c.pin_entry_photo_kinds.length > 0
    ? c.pin_entry_photo_kinds.map((k: any) => String(k).toLowerCase())
    : ['guest', 'delivery'];
  const [photoForGuest,    setPhotoForGuest]    = useState<boolean>(initialKinds.includes('guest'));
  const [photoForDelivery, setPhotoForDelivery] = useState<boolean>(initialKinds.includes('delivery'));
  const [photoForTenant,   setPhotoForTenant]   = useState<boolean>(initialKinds.includes('tenant') || initialKinds.includes('pin'));

  const handleSave = async () => {
    const apply: string[] = [];
    if (geofenceWeb) apply.push('web');
    if (geofenceMobile) apply.push('mobile');
    const photoKinds: string[] = [];
    if (photoForGuest) photoKinds.push('guest');
    if (photoForDelivery) photoKinds.push('delivery');
    if (photoForTenant) photoKinds.push('tenant');
    await onSave({
      allow_phone_call:               allowPhone,
      pin_access_enabled:             pinEnabled,
      face_access_enabled:            faceEnabled,
      guest_pin_access_enabled:       guestPinEnabled,
      require_directory_code:         requireDirCode,
      directory_access_code:          dirAccessCode || null,
      visitor_code_response:          visitorCodeResponse,
      auto_lock_enabled:              autoLockEnabled,
      auto_lock_start:                autoLockStart || null,
      auto_lock_end:                  autoLockEnd || null,
      require_visitor_name:           requireVisName,
      require_visitor_phone:          requireVisPhone,
      private_directory:              privateDirectory,
      log_all_visitor_access:         logAllAccess,
      visitor_rate_limit:             visitorRateLimit ? Number(visitorRateLimit) : null,
      block_repeated_no_answers:      blockNoAnswers,
      max_no_answers_before_block:    maxNoAnswers ? Number(maxNoAnswers) : null,
      geofence_enabled:               geofenceEnabled,
      geofence_lat:                   geofenceLat ? Number(geofenceLat) : null,
      geofence_lng:                   geofenceLng ? Number(geofenceLng) : null,
      geofence_radius_m:              geofenceRadius ? Number(geofenceRadius) : null,
      geofence_apply_to:              apply,
      pin_entry_photo_enabled:        pinPhotoEnabled,
      pin_entry_photo_kinds:          pinPhotoEnabled ? photoKinds : null,
      pin_entry_photo_required:       pinPhotoEnabled ? pinPhotoRequired : false,
    } as any);
    onClose();
  };

  const labelCls = 'block text-xs font-medium text-gray-600 mb-1';
  const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-accent-500 focus:border-accent-500';
  const checkRow = (checked: boolean, onChange: (v: boolean) => void, label: string, hint?: string, disabled = false) => (
    <label className={`flex items-start gap-2 py-1 ${disabled ? 'opacity-60' : 'cursor-pointer'}`}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="rounded border-gray-300 text-accent-600 focus:ring-accent-500 h-4 w-4 flex-shrink-0 mt-0.5"
      />
      <span>
        <span className="text-sm text-gray-700">{label}</span>
        {hint && <span className="block text-[11px] text-gray-400">{hint}</span>}
      </span>
    </label>
  );

  return (
    <div className="space-y-4 max-h-[65vh] overflow-y-auto">
      {/* Entry methods */}
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">Entry Methods</p>
        <div className="space-y-0.5">
          {checkRow(allowPhone, setAllowPhone, 'Allow phone calls', 'Master switch — hides the phone call button on the visitor directory.')}
          {checkRow(pinEnabled, setPinEnabled, 'Show PIN pad', 'Master keypad visibility for tenant, guest, and delivery codes on this intercom. Each code type also requires its own property permission.')}
          {checkRow(faceEnabled, setFaceEnabled, 'Show Face ID', propertyDefaults.face_access_enabled === true ? 'Resident uses face recognition (AWS Rekognition) to unlock.' : 'Inactive until Face ID is enabled for this property under Access Controls (premium).', propertyDefaults.face_access_enabled !== true)}
          {checkRow(guestPinEnabled, setGuestPinEnabled, 'Allow guest PINs', 'Residents can mint short-lived PINs for visitors.')}
        </div>
      </div>

      {/* Directory gate */}
      <div className="pt-3 border-t border-gray-100 space-y-2">
        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Directory Gate</p>
        {checkRow(requireDirCode, setRequireDirCode, 'Require code to view directory', 'When on, visitors must enter the code below before seeing unit names.')}
        {requireDirCode && (
          <div>
            <label className={labelCls}>Directory Access Code</label>
            <input
              type="text"
              value={dirAccessCode}
              onChange={(e) => setDirAccessCode(e.target.value)}
              placeholder="e.g. 4-8 digits"
              className={inputCls}
            />
          </div>
        )}
        <div>
          <label className={labelCls}>When a visitor pass code is entered</label>
          <select value={visitorCodeResponse} onChange={(e) => setVisitorCodeResponse(e.target.value as any)} className={inputCls}>
            <option value="grant_only">Grant entry only</option>
            <option value="grant_and_open_pass">Grant entry and open Visitor Pass</option>
          </select>
          <span className="block text-[11px] text-gray-400 mt-1">An incomplete pass opens at secure phone verification and required onboarding.</span>
        </div>
      </div>

      {/* Auto-lock window */}
      <div className="pt-3 border-t border-gray-100 space-y-2">
        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Auto-Lock Window</p>
        {checkRow(autoLockEnabled, setAutoLockEnabled, 'Auto-lock directory at night', 'Within this window the visitor directory is replaced with a closed-for-the-night screen.')}
        {autoLockEnabled && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Start (24h)</label>
              <input type="time" value={autoLockStart} onChange={(e) => setAutoLockStart(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>End (24h)</label>
              <input type="time" value={autoLockEnd} onChange={(e) => setAutoLockEnd(e.target.value)} className={inputCls} />
            </div>
          </div>
        )}
      </div>

      {/* Visitor info */}
      <div className="pt-3 border-t border-gray-100 space-y-1">
        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">Visitor Info Required</p>
        {checkRow(requireVisName, setRequireVisName, 'Require visitor name before calling')}
        {checkRow(requireVisPhone, setRequireVisPhone, 'Require visitor phone before calling')}
      </div>

      {/* Privacy / rate limit */}
      <div className="pt-3 border-t border-gray-100 space-y-2">
        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Privacy & Rate Limit</p>
        {checkRow(privateDirectory, setPrivateDirectory, 'Private directory (search-only)', 'Hides the unit list. Visitor must type a unit number to call.')}
        {checkRow(logAllAccess, setLogAllAccess, 'Log every visitor page view', 'Stores each visit with IP + user agent for audit.')}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Visitor Rate Limit (per minute)</label>
            <input
              type="number"
              min={1}
              max={60}
              value={visitorRateLimit}
              onChange={(e) => setVisitorRateLimit(e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Max No-Answers Before Block</label>
            <input
              type="number"
              min={1}
              max={20}
              value={maxNoAnswers}
              onChange={(e) => setMaxNoAnswers(e.target.value)}
              className={inputCls}
            />
          </div>
        </div>
        {checkRow(blockNoAnswers, setBlockNoAnswers, 'Block visitors after repeated no-answers')}
      </div>

      {/* Geofence */}
      <div className="pt-3 border-t border-gray-100 space-y-2">
        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Geofence</p>
        {checkRow(geofenceEnabled, setGeofenceEnabled, 'Restrict visitor access to a physical area', 'Visitors outside the geofence see a "you must be on site" screen.')}
        {geofenceEnabled && (
          <>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className={labelCls}>Latitude</label>
                <input type="text" inputMode="decimal" value={geofenceLat} onChange={(e) => setGeofenceLat(e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Longitude</label>
                <input type="text" inputMode="decimal" value={geofenceLng} onChange={(e) => setGeofenceLng(e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Radius (m)</label>
                <input type="number" min={10} max={5000} value={geofenceRadius} onChange={(e) => setGeofenceRadius(e.target.value)} className={inputCls} />
              </div>
            </div>
            <div className="flex gap-4 pt-1">
              {checkRow(geofenceWeb, setGeofenceWeb, 'Apply to web visitors')}
              {checkRow(geofenceMobile, setGeofenceMobile, 'Apply to mobile app')}
            </div>
          </>
        )}
      </div>

      {/* ── PIN-entry photo capture ─────────────────────────────────── */}
      <div className="border-t border-gray-100 pt-4 space-y-2">
        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">PIN-entry photo capture</p>
        {checkRow(pinPhotoEnabled, setPinPhotoEnabled, 'Take a photo of whoever enters a PIN',
          'When on, the visitor web page asks for camera access and captures a single still on PIN entry. The photo is stored on the unlock event and shown in device history. Requires HTTPS + camera permission.')}
        {pinPhotoEnabled && (
          <>
            <div className="ml-7 grid grid-cols-3 gap-2 pt-1">
              {checkRow(photoForGuest,    setPhotoForGuest,    'On guest PINs')}
              {checkRow(photoForDelivery, setPhotoForDelivery, 'On delivery PINs')}
              {checkRow(photoForTenant,   setPhotoForTenant,   'On tenant PINs')}
            </div>
            <div className="ml-7 pt-1">
              {checkRow(pinPhotoRequired, setPinPhotoRequired,
                'Block unlock if no photo can be captured',
                'When on, the door stays locked if the browser/kiosk can\'t take a photo (camera denied, no camera, blocked by browser). Use this to guarantee every PIN entry has an identity record.')}
            </div>
          </>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} disabled={saving} className="bg-accent-500 hover:bg-accent-600">
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
};

// ── Mobile App modal body ─────────────────────────────────────────────────
// Tenant-app-side controls per intercom: who can mint guest PINs / how many,
// legacy resident management and minor (under-age) controls.
// ── Delete confirmation modal ─────────────────────────────────────────────
// Double-gated: the PM must type both the literal word "delete" AND the
// exact intercom name. Either alone won't enable the destructive button.
const DeleteIntercomModalBody: React.FC<{
  intercomName: string;
  onConfirm: () => void;
  onClose: () => void;
}> = ({ intercomName, onConfirm, onClose }) => {
  const [deleteWord, setDeleteWord] = useState('');
  const [nameInput, setNameInput] = useState('');
  const wordOk = deleteWord.trim().toLowerCase() === 'delete';
  const nameOk = nameInput.trim() === intercomName.trim();
  const canDelete = wordOk && nameOk;
  const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 font-mono';
  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800">
        <p className="font-semibold mb-1">This action is permanent.</p>
        <p>
          Deleting <span className="font-mono font-semibold">{intercomName}</span> removes its phone
          assignment, kiosk pairing code, door assignments, and access rules. Tenants will no longer
          be able to call this intercom or unlock its doors. This cannot be undone.
        </p>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Type <span className="font-mono font-semibold text-red-600">delete</span> to confirm
        </label>
        <input
          type="text"
          value={deleteWord}
          onChange={(e) => setDeleteWord(e.target.value)}
          placeholder="delete"
          autoComplete="off"
          className={inputCls}
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Type the intercom name <span className="font-mono font-semibold text-red-600">{intercomName}</span> to confirm
        </label>
        <input
          type="text"
          value={nameInput}
          onChange={(e) => setNameInput(e.target.value)}
          placeholder={intercomName}
          autoComplete="off"
          className={inputCls}
        />
      </div>
      <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={!canDelete}
          className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
            canDelete
              ? 'bg-red-600 hover:bg-red-700 text-white shadow-sm'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          Delete intercom permanently
        </button>
      </div>
    </div>
  );
};

// ── Delivery Settings modal body ──────────────────────────────────────────
// PM controls per-intercom delivery PIN behavior: whether tenants can mint
// delivery codes, which doors a delivery code can open, and a free-form
// message shown to the driver on the visitor page.
const DeliveryModalBody: React.FC<{
  config: IntercomConfig;
  devices: any[];
  saving: boolean;
  onSave: (patch: Partial<IntercomConfig>) => Promise<void>;
  onClose: () => void;
}> = ({ config, devices, saving, onSave, onClose }) => {
  const c = config as any;
  const unlockDevices = eligibleUnlockDevices(devices);
  const [allowDelivery,    setAllowDelivery]    = useState<boolean>(c.allow_tenant_create_deliveries !== false);
  const [doorIds,          setDoorIds]          = useState<string[]>(
    Array.isArray(c.delivery_door_ids) ? c.delivery_door_ids.map(String) : []
  );
  const [deliveryMessage,  setDeliveryMessage]  = useState<string>(c.delivery_message ?? '');
  const [maxUses,          setMaxUses]          = useState<string>(String(c.delivery_max_uses ?? '1'));
  const [requireGeofence,  setRequireGeofence]  = useState<boolean>(c.delivery_require_geofence === true);

  const toggleDoor = (k: string) => {
    setDoorIds((prev) => prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]);
  };

  const handleSave = async () => {
    const usesN = Math.max(1, parseInt(maxUses, 10) || 1);
    await onSave({
      allow_tenant_create_deliveries: allowDelivery,
      delivery_door_ids:               doorIds,
      delivery_message:                deliveryMessage.trim() || null,
      delivery_max_uses:               usesN,
      delivery_require_geofence:       requireGeofence,
    } as any);
    onClose();
  };

  const labelCls = 'block text-xs font-medium text-gray-600 mb-1';
  const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-accent-500 focus:border-accent-500';

  return (
    <div className="space-y-4 max-h-[65vh] overflow-y-auto">
      <div className="space-y-2">
        <label className="flex items-start gap-2 cursor-pointer py-1">
          <input
            type="checkbox"
            checked={allowDelivery}
            onChange={(e) => setAllowDelivery(e.target.checked)}
            className="rounded border-gray-300 text-accent-600 focus:ring-accent-500 h-4 w-4 flex-shrink-0 mt-0.5"
          />
          <span>
            <span className="text-sm text-gray-700">Allow tenants to create delivery PINs</span>
            <span className="block text-[11px] text-gray-400">
              When on, tenants can mint a one-time PIN for couriers from the mobile app and tenant portal.
            </span>
          </span>
        </label>
      </div>

      <div>
        <label className={labelCls}>Delivery doors ({doorIds.length} selected)</label>
        {unlockDevices.length === 0 ? (
          <p className="text-[11px] text-gray-500 italic px-3 py-2 border border-gray-200 rounded-lg">
            No devices on this property yet.
          </p>
        ) : (
          <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
            {unlockDevices.map((d) => {
              const k = deviceAccessId(d);
              const checked = doorIds.includes(k);
              return (
                <label key={k} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 border-b border-gray-50 last:border-b-0">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleDoor(k)}
                    className="w-4 h-4 rounded border-gray-300 text-accent-600 focus:ring-accent-500"
                  />
                  <span className="flex-1 text-gray-700 truncate">{deviceLabel(d)}</span>
                </label>
              );
            })}
          </div>
        )}
        <p className="text-[11px] text-gray-400 mt-1">
          Doors a delivery PIN is allowed to open at this intercom. Leave empty to allow all of this intercom's doors.
        </p>
      </div>

      <div>
        <label className={labelCls}>Delivery message</label>
        <textarea
          value={deliveryMessage}
          onChange={(e) => setDeliveryMessage(e.target.value)}
          rows={4}
          placeholder="Building-wide instructions for delivery drivers — e.g., Leave packages inside the lobby on the right."
          className={`${inputCls} resize-y min-h-[88px]`}
        />
        <p className="text-[11px] text-gray-400 mt-1">
          Shown to the driver on the visitor web page after they enter a valid delivery code.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Max uses per code</label>
          <input
            type="number"
            min={1}
            max={20}
            value={maxUses}
            onChange={(e) => setMaxUses(e.target.value)}
            className={inputCls}
          />
          <p className="text-[11px] text-gray-400 mt-1">Default 1 — codes self-expire on first use.</p>
        </div>
        <div className="flex items-start pt-5">
          <label className="flex items-start gap-2 cursor-pointer py-1">
            <input
              type="checkbox"
              checked={requireGeofence}
              onChange={(e) => setRequireGeofence(e.target.checked)}
              className="rounded border-gray-300 text-accent-600 focus:ring-accent-500 h-4 w-4 flex-shrink-0 mt-0.5"
            />
            <span>
              <span className="text-sm text-gray-700">Require location at door</span>
              <span className="block text-[11px] text-gray-400">
                Only takes effect when this property's geofence is also enabled in Access &amp; Security. Visitor surfaces never ask for location unless the property geofence is on.
              </span>
            </span>
          </label>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} disabled={saving} className="bg-accent-500 hover:bg-accent-600">
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
};

const MobileModalBody: React.FC<{
  config: IntercomConfig;
  devices: any[];
  saving: boolean;
  onSave: (patch: Partial<IntercomConfig>) => Promise<void>;
  onClose: () => void;
}> = ({ config, devices, saving, onSave, onClose }) => {
  const c = config as any;
  // Every unlock-capable device on the property (not just doors wired to this
  // intercom). Cameras, sirens and generic relays are deliberately excluded.
  const propertyDoorDevices = eligibleUnlockDevices(devices);
  // PM-controlled subset visible to tenants in the mobile app. null
  // means unrestricted (default: every property door is visible). When
  // the PM first unchecks a box we materialize to an explicit list of
  // all-property-doors-minus-that-one.
  const initialVisible: string[] | null = Array.isArray(c.tenant_visible_door_ids)
    ? (c.tenant_visible_door_ids as any[]).map(String)
    : null;
  const [tenantVisible, setTenantVisible] = useState<string[] | null>(initialVisible);

  // Cameras follow the opposite default — privacy-first, hidden until the
  // PM opts a specific camera in. `tenant_visible_camera_ids` is an
  // explicit allow-list; missing/empty = no tenant sees any camera on
  // this property until the PM checks the box.
  const isCameraLike = (d: any): boolean => {
    const t = String(d?.device_type ?? d?.type ?? '').toLowerCase();
    return ['camera', 'doorbell', 'ipcamera'].includes(t) || d?.acs_provider === 'turing';
  };
  const propertyCameraDevices = devices.filter(isCameraLike);
  const initialVisibleCams: string[] = Array.isArray(c.tenant_visible_camera_ids)
    ? (c.tenant_visible_camera_ids as any[]).map(String)
    : [];
  const [tenantVisibleCameras, setTenantVisibleCameras] = useState<string[]>(initialVisibleCams);
  const isCameraVisibleToTenants = (k: string) => tenantVisibleCameras.includes(k);
  const toggleTenantVisibleCamera = (k: string) => {
    setTenantVisibleCameras((tv) => tv.includes(k) ? tv.filter((x) => x !== k) : [...tv, k]);
  };
  const isDoorVisibleToTenants = (k: string): boolean => {
    if (tenantVisible === null) return true;
    return tenantVisible.includes(k);
  };
  const toggleTenantVisible = (k: string) => {
    setTenantVisible((tv) => {
      if (tv === null) {
        // First unchecked door materializes from "all visible" to an
        // explicit list of every property door except this one.
        return propertyDoorDevices.map(deviceKey).filter((x) => x !== k);
      }
      return tv.includes(k) ? tv.filter((x) => x !== k) : [...tv, k];
    });
  };

  const [allowGuestCreate,    setAllowGuestCreate]    = useState<boolean>(c.allow_tenant_create_guests !== false);
  const [maxGuestsPerTenant,  setMaxGuestsPerTenant]  = useState<string>(String(c.default_max_guests_per_tenant ?? '10'));
  const [maxGuestDays,        setMaxGuestDays]        = useState<string>(String(c.default_max_guest_duration_days ?? '30'));
  const [guestPinLength,      setGuestPinLength]      = useState<string>(String(c.guest_pin_length ?? '6'));
  const [guestAppEnabled,     setGuestAppEnabled]     = useState<boolean>(c.guest_app_access_enabled === true);
  const [notifyMgrGuest,      setNotifyMgrGuest]      = useState<boolean>(c.notify_manager_on_guest_create === true);
  const [notifyTenantGuest,   setNotifyTenantGuest]   = useState<boolean>(c.notify_tenant_on_guest_pin_use === true);
  const [allowDeliveryCreate, setAllowDeliveryCreate] = useState<boolean>(c.allow_tenant_create_deliveries !== false);
  const [allowSubCreate,      setAllowSubCreate]      = useState<boolean>(c.allow_tenant_create_subtenants !== false);
  const [subtenantReceive,    setSubtenantReceive]    = useState<boolean>(c.subtenant_receive_calls === true);
  const [subDoorDefault,      setSubDoorDefault]      = useState<string>(c.subtenant_door_access_default ?? 'Same as Tenant');
  const [minorAgeThreshold,   setMinorAgeThreshold]   = useState<string>(String(c.minor_age_threshold ?? '18'));
  const [minorCallBlock,      setMinorCallBlock]      = useState<boolean>(c.minor_call_block === true);

  const handleSave = async () => {
    await onSave({
      tenant_visible_door_ids:          tenantVisible,
      tenant_visible_camera_ids:        tenantVisibleCameras,
      allow_tenant_create_guests:       allowGuestCreate,
      default_max_guests_per_tenant:    maxGuestsPerTenant ? Number(maxGuestsPerTenant) : null,
      default_max_guest_duration_days:  maxGuestDays ? Number(maxGuestDays) : null,
      guest_pin_length:                 guestPinLength ? Number(guestPinLength) : null,
      guest_app_access_enabled:         guestAppEnabled,
      notify_manager_on_guest_create:   notifyMgrGuest,
      notify_tenant_on_guest_pin_use:   notifyTenantGuest,
      allow_tenant_create_deliveries:   allowDeliveryCreate,
      allow_tenant_create_subtenants:   allowSubCreate,
      subtenant_receive_calls:          subtenantReceive,
      subtenant_door_access_default:    subDoorDefault || null,
      minor_age_threshold:              minorAgeThreshold ? Number(minorAgeThreshold) : null,
      minor_call_block:                 minorCallBlock,
    } as any);
    onClose();
  };

  const labelCls = 'block text-xs font-medium text-gray-600 mb-1';
  const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-accent-500 focus:border-accent-500';
  const checkRow = (checked: boolean, onChange: (v: boolean) => void, label: string, hint?: string) => (
    <label className="flex items-start gap-2 cursor-pointer py-1">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="rounded border-gray-300 text-accent-600 focus:ring-accent-500 h-4 w-4 flex-shrink-0 mt-0.5"
      />
      <span>
        <span className="text-sm text-gray-700">{label}</span>
        {hint && <span className="block text-[11px] text-gray-400">{hint}</span>}
      </span>
    </label>
  );

  return (
    <div className="space-y-4 max-h-[65vh] overflow-y-auto">
      {/* Doors visible to tenants — PM-controlled subset of every door
          on the PROPERTY (not just this intercom's). Unchecked = hidden
          from tenants in the mobile app (Doors tab, Add Guest picker)
          for any tenant who doesn't have a per-tenant override granting it. */}
      <div className="space-y-2">
        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Doors visible to tenants</p>
        {propertyDoorDevices.length === 0 ? (
          <p className="text-[11px] text-gray-500 italic px-3 py-2 border border-gray-200 rounded-lg">
            No doors configured on this property yet.
          </p>
        ) : (
          <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
            {propertyDoorDevices.map((d) => {
              const k = deviceKey(d);
              const checked = isDoorVisibleToTenants(k);
              const name = (d.name && String(d.name).trim()) || 'Door';
              return (
                <label key={k} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 border-b border-gray-50 last:border-b-0">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleTenantVisible(k)}
                    className="w-4 h-4 rounded border-gray-300 text-accent-600 focus:ring-accent-500"
                  />
                  <span className="flex-1 text-gray-700 truncate">{name}</span>
                </label>
              );
            })}
          </div>
        )}
        <p className="text-[11px] text-gray-400 mt-1">
          Hidden doors don't appear in any tenant's mobile-app Doors tab or guest-creation picker for this property. Per-tenant overrides on the People page can re-grant access for individual tenants.
        </p>
      </div>

      {/* Cameras visible to tenants — opt-in allow-list. Empty = no
          cameras visible to any tenant on this property. */}
      <div className="pt-3 border-t border-gray-100 space-y-2">
        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Cameras visible to tenants</p>
        {propertyCameraDevices.length === 0 ? (
          <p className="text-[11px] text-gray-500 italic px-3 py-2 border border-gray-200 rounded-lg">
            No cameras synced to this property yet.
          </p>
        ) : (
          <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
            {propertyCameraDevices.map((d) => {
              const k = String(d.id);
              const checked = isCameraVisibleToTenants(k);
              const name = (d.name && String(d.name).trim()) || 'Camera';
              return (
                <label key={k} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 border-b border-gray-50 last:border-b-0">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleTenantVisibleCamera(k)}
                    className="w-4 h-4 rounded border-gray-300 text-accent-600 focus:ring-accent-500"
                  />
                  <span className="flex-1 text-gray-700 truncate">{name}</span>
                </label>
              );
            })}
          </div>
        )}
        <p className="text-[11px] text-gray-400 mt-1">
          Cameras are hidden by default. Only checked cameras appear in any tenant's mobile-app Cameras tab. Per-tenant overrides on the People page can grant or hide individual cameras on top of this baseline.
        </p>
      </div>

      {/* Guest controls */}
      <div className="pt-3 border-t border-gray-100 space-y-2">
        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Guest PINs</p>
        {checkRow(allowGuestCreate, setAllowGuestCreate, 'Tenants can mint guest PINs', 'When off, only the PM can create guest PINs.')}
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className={labelCls}>Max Active Guests per Tenant</label>
            <input type="number" min={0} max={100} value={maxGuestsPerTenant} onChange={(e) => setMaxGuestsPerTenant(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Max Guest Duration (days)</label>
            <input type="number" min={1} max={365} value={maxGuestDays} onChange={(e) => setMaxGuestDays(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Guest PIN Length</label>
            <select value={guestPinLength} onChange={(e) => setGuestPinLength(e.target.value)} className={inputCls}>
              <option value="4">4 digits</option>
              <option value="6">6 digits</option>
              <option value="8">8 digits</option>
            </select>
          </div>
        </div>
        {checkRow(guestAppEnabled, setGuestAppEnabled, 'Guests can use the mobile app', 'Lets guests open the resident app with their PIN — limited to their access window.')}
        {checkRow(notifyMgrGuest, setNotifyMgrGuest, 'Notify PM when a guest PIN is created')}
        {checkRow(notifyTenantGuest, setNotifyTenantGuest, 'Notify tenant when a guest PIN is used')}
      </div>

      {/* Delivery PINs — tenants mint one-time codes for couriers with an
          arrival window + notes. Backend (intrlock-intercom-api-v2
          POST /intercom/create-delivery-code) checks this flag and refuses
          the request when off. */}
      <div className="pt-3 border-t border-gray-100 space-y-2">
        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Delivery PINs</p>
        {checkRow(allowDeliveryCreate, setAllowDeliveryCreate, 'Tenants can create delivery codes', 'Mobile-app tenants mint a one-time PIN with carrier + arrival window for couriers. Off blocks the Deliveries tab on iOS / web for this property.')}
      </div>

      {/* Minor controls */}
      <div className="pt-3 border-t border-gray-100 space-y-2">
        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Minor (Under-Age) Controls</p>
        <div>
          <label className={labelCls}>Minor Age Threshold</label>
          <input type="number" min={0} max={21} value={minorAgeThreshold} onChange={(e) => setMinorAgeThreshold(e.target.value)} className={inputCls} />
          <p className="text-[11px] text-gray-400 mt-1">Tenants under this age are treated as minors for the next rule.</p>
        </div>
        {checkRow(minorCallBlock, setMinorCallBlock, 'Block visitor calls to minors', 'Visitors selecting a minor see a no-answer message instead of ringing them.')}
      </div>

      <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} disabled={saving} className="bg-accent-500 hover:bg-accent-600">
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
};

// ── Residents modal body ──────────────────────────────────────────────────
// Per-unit visibility toggle. Each unit row shows the tenants in that unit
// (fetched from /people on open) and a Hidden/Shown toggle that writes to
// intercom_config.hidden_unit_ids. Saves immediately on each toggle — no
// separate Save button, matches the current Manage flow's UX.
const ResidentsModalBody: React.FC<{
  config: IntercomConfig;
  units: Array<{ id: string | number; unit_number: string; floor?: string | null }>;
  saving: boolean;
  onSave: (patch: Partial<IntercomConfig>) => Promise<void>;
  onClose: () => void;
}> = ({ config, units, saving, onSave, onClose }) => {
  const c = config as any;
  const [hiddenUnitIds, setHiddenUnitIds] = useState<Set<string>>(new Set((c.hidden_unit_ids ?? []).map(String)));
  const [tenantsByUnit, setTenantsByUnit] = useState<Map<string, Array<{ id: string; first_name: string; last_name: string; is_primary?: boolean; person_type?: string; activated?: boolean }>>>(new Map());
  const [search, setSearch] = useState('');
  const [loadingTenants, setLoadingTenants] = useState(true);

  useEffect(() => {
    let abort = false;
    (async () => {
      setLoadingTenants(true);
      try {
        const items = await apiList('tenants', { property_id: String(c.property_id), per_page: 500 });
        const arr: any[] = Array.isArray(items) ? items : [];
        const byUnit = new Map<string, any[]>();
        for (const t of arr) {
          if (t.status && t.status !== 'active') continue;
          const uid = String(t.unit_id ?? '');
          if (!uid) continue;
          if (!byUnit.has(uid)) byUnit.set(uid, []);
          byUnit.get(uid)!.push({
            id: String(t.uuid ?? t.id ?? ''),
            first_name: String(t.first_name ?? '').trim(),
            last_name: String(t.last_name ?? '').trim(),
            is_primary: !!t.is_primary,
            person_type: t.person_type,
            activated: !!t.activation_id,
          });
        }
        // Sort each unit's tenants — primary first, then by last name.
        for (const list of Array.from(byUnit.values())) {
          list.sort((a: any, b: any) => {
            if (!!a.is_primary !== !!b.is_primary) return a.is_primary ? -1 : 1;
            return a.last_name.localeCompare(b.last_name);
          });
        }
        if (!abort) setTenantsByUnit(byUnit);
      } catch {
        if (!abort) setTenantsByUnit(new Map());
      } finally {
        if (!abort) setLoadingTenants(false);
      }
    })();
    return () => { abort = true; };
  }, [c.property_id]);

  const toggleUnit = async (uid: string) => {
    const next = new Set(hiddenUnitIds);
    if (next.has(uid)) next.delete(uid); else next.add(uid);
    setHiddenUnitIds(next);
    await onSave({ hidden_unit_ids: Array.from(next) } as any);
  };

  const q = search.trim().toLowerCase();
  const sortedUnits = [...units].sort((a, b) => String(a.unit_number).localeCompare(String(b.unit_number), undefined, { numeric: true }));
  const filtered = !q ? sortedUnits : sortedUnits.filter((u) => {
    const tenants = tenantsByUnit.get(String(u.id)) ?? [];
    return String(u.unit_number).toLowerCase().includes(q) ||
      tenants.some((t) => `${t.first_name} ${t.last_name}`.toLowerCase().includes(q));
  });

  return (
    <div className="space-y-3 max-h-[65vh] overflow-y-auto">
      <div className="flex items-center justify-between gap-2">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by unit number or tenant…"
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
        />
        <span className="text-xs text-gray-500 whitespace-nowrap">
          {units.length - hiddenUnitIds.size}/{units.length} visible
        </span>
      </div>

      {loadingTenants && (
        <p className="text-xs text-gray-400 italic">Loading residents…</p>
      )}

      {filtered.length === 0 ? (
        <p className="text-sm text-gray-500 italic p-4 text-center">No units match "{search}".</p>
      ) : (
        <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
          {filtered.map((u) => {
            const uid = String(u.id);
            const visible = !hiddenUnitIds.has(uid);
            const tenants = tenantsByUnit.get(uid) ?? [];
            return (
              <div key={uid} className="px-3 py-2 flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-mono text-sm font-semibold text-gray-900">Unit {u.unit_number}</span>
                    {u.floor && <span className="text-[10px] text-gray-500">Floor {u.floor}</span>}
                  </div>
                  {tenants.length === 0 ? (
                    <p className="text-[11px] text-gray-400 italic">Vacant</p>
                  ) : (
                    <ul className="space-y-0.5">
                      {tenants.map((t) => (
                        <li key={t.id} className="text-[12px] text-gray-700 flex items-center gap-1.5">
                          <span>{t.first_name} {t.last_name}</span>
                          {t.is_primary && <span className="text-[9px] uppercase tracking-wider px-1 py-0.5 rounded bg-accent-100 text-accent-700">Primary</span>}
                          {t.activated && <span className="text-[9px] uppercase tracking-wider px-1 py-0.5 rounded bg-green-100 text-green-700">App</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => toggleUnit(uid)}
                  disabled={saving}
                  className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-[12px] font-semibold transition-colors ${
                    visible
                      ? 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200 border border-gray-200'
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${visible ? 'bg-green-500' : 'bg-gray-400'}`} />
                  {visible ? 'Shown' : 'Hidden'}
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex justify-end pt-2 border-t border-gray-100">
        <Button variant="secondary" onClick={onClose}>Done</Button>
      </div>
    </div>
  );
};
